import { useEffect, useRef, useCallback, useState } from 'react';
import { HubConnectionBuilder, HubConnection, HubConnectionState, LogLevel, } from '@microsoft/signalr';
import { useNavigate } from 'react-router-dom';
import { useMovieStore, useAuthStore } from '../stores';
import { refreshAccessToken } from '../util/tokenRefresh';
import { resolveAssetUrl } from '../util/api';
import { extractAssetPathFromBody } from '../util/imageBody';
// ============================================================================
// Configuration
// ============================================================================
const MOVIE_HUB_URL = '/movieHub';
const TIMELINE_HUB_URL = '/timelineHub';
const RECONNECT_DELAYS = [0, 2000, 5000, 10000, 30000]; // Reconnect backoff
// ============================================================================
// Helper Functions
// ============================================================================
/**
 * Check if an error indicates unauthorized access (401/403)
 */
function isUnauthorizedError(err) {
    if (!err)
        return false;
    const message = err instanceof Error ? err.message.toLowerCase() : String(err).toLowerCase();
    // Check for common unauthorized error patterns
    return (message.includes('unauthorized') ||
        message.includes('401') ||
        message.includes('403') ||
        message.includes('forbidden') ||
        message.includes('not authorized') ||
        message.includes('authentication failed') ||
        message.includes('token expired') ||
        message.includes('invalid token'));
}
function normalizeObjectSize(value) {
    return typeof value === 'number' && value > 0 ? Math.round(value) : 100;
}
// ============================================================================
// Hook Implementation
// ============================================================================
export function useSignalR(autoConnect = true) {
    const movieConnectionRef = useRef(null);
    const timelineConnectionRef = useRef(null);
    const handleAuthFailureRef = useRef(null);
    const [status, setStatus] = useState('Disconnected');
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const accessToken = useAuthStore((state) => state.accessToken);
    const logout = useAuthStore((state) => state.logout);
    // Get store actions for syncing (used in server-to-client event handlers)
    const { addSceneObject, updateSceneObject, deleteSceneObject } = useMovieStore();
    const createConnection = useCallback((hubUrl, hubName) => {
        const connection = new HubConnectionBuilder()
            .withUrl(hubUrl, {
            accessTokenFactory: () => useAuthStore.getState().accessToken || '',
        })
            .withAutomaticReconnect(RECONNECT_DELAYS)
            .configureLogging(LogLevel.Information)
            .build();
        // Connection lifecycle events
        connection.onreconnecting((err) => {
            console.log(`[SignalR:${hubName}] Reconnecting...`, err);
            setStatus('Reconnecting');
            setError(err?.message ?? null);
        });
        connection.onreconnected((connectionId) => {
            console.log(`[SignalR:${hubName}] Reconnected:`, connectionId);
            setStatus('Connected');
            setError(null);
        });
        connection.onclose((err) => {
            console.log(`[SignalR:${hubName}] Connection closed`, err);
            setStatus('Disconnected');
            if (err) {
                setError(err.message);
                // Check if the error is due to unauthorized access
                if (isUnauthorizedError(err)) {
                    console.warn('[SignalR] Unauthorized error detected, attempting token refresh...');
                    void handleAuthFailureRef.current?.();
                }
            }
        });
        // Server-to-client events
        // Connection lifecycle event
        connection.on('Connected', (data) => {
            console.log(`[SignalR:${hubName}] Connected:`, data);
            console.log(`✅ ${data.message}`);
            console.log(`   Connection ID: ${data.connectionId}`);
            console.log(`   User ID: ${data.userId}`);
        });
        if (hubName === 'MovieHub') {
            // Real-time collaboration events (if backend broadcasts)
            connection.on('SceneObjectUpdated', (sceneObject) => {
                console.log('[SignalR] SceneObjectUpdated', sceneObject);
                // Convert and update store
                updateSceneObject(String(sceneObject.id), {
                    x: sceneObject.dropPosition?.left ?? 0,
                    y: sceneObject.dropPosition?.top ?? 0,
                    visible: sceneObject.isVisible,
                    locked: sceneObject.isLocked,
                });
            });
            connection.on('SceneObjectCreated', (sceneObject) => {
                console.log('[SignalR] SceneObjectCreated', sceneObject);
                addSceneObject(convertSceneObjectResponse(sceneObject));
            });
            connection.on('SceneObjectDeleted', (sceneObject) => {
                console.log('[SignalR] SceneObjectDeleted', sceneObject);
                deleteSceneObject(String(sceneObject.id));
            });
        }
        return connection;
    }, [updateSceneObject, addSceneObject, deleteSceneObject]);
    const waitForDisconnected = useCallback(async () => {
        const maxWaitMs = 2000;
        const start = Date.now();
        const refs = [movieConnectionRef, timelineConnectionRef];
        while (refs.some((r) => r.current && r.current.state === HubConnectionState.Disconnecting)) {
            await new Promise((resolve) => setTimeout(resolve, 50));
            if (Date.now() - start > maxWaitMs) {
                break;
            }
        }
    }, []);
    // Connect
    const connect = useCallback(async () => {
        if (movieConnectionRef.current?.state === HubConnectionState.Disconnecting ||
            timelineConnectionRef.current?.state === HubConnectionState.Disconnecting) {
            console.log('[SignalR] Waiting for disconnect to complete...');
            await waitForDisconnected();
        }
        const movieState = movieConnectionRef.current?.state;
        const timelineState = timelineConnectionRef.current?.state;
        const movieReady = !movieState || movieState === HubConnectionState.Disconnected;
        const timelineReady = !timelineState || timelineState === HubConnectionState.Disconnected;
        // Only proceed if we're not already trying to connect or already connected
        if (!movieReady || !timelineReady) {
            console.log('[SignalR] Connection attempt skipped: current states are', { movieState, timelineState });
            return;
        }
        try {
            setStatus('Connecting');
            setError(null);
            if (!movieConnectionRef.current) {
                movieConnectionRef.current = createConnection(MOVIE_HUB_URL, 'MovieHub');
            }
            if (!timelineConnectionRef.current) {
                timelineConnectionRef.current = createConnection(TIMELINE_HUB_URL, 'TimelineHub');
            }
            await movieConnectionRef.current.start();
            await timelineConnectionRef.current.start();
            console.log('[SignalR] Connected (MovieHub + TimelineHub)');
            setStatus('Connected');
        }
        catch (err) {
            // If the connection was stopped during negotiation (e.g. by a rapid unmount in Strict Mode),
            // we can ignore this error as it's an expected part of the React lifecycle.
            const abortMessage = err instanceof Error ? err.message : String(err);
            if (err instanceof Error && (err.name === 'AbortError' || abortMessage.includes('stopped during negotiation'))) {
                console.log('[SignalR] Connection aborted during negotiation (expected during unmount)');
                return;
            }
            console.error('[SignalR] Connection failed:', err);
            setStatus('Error');
            setError(err instanceof Error ? err.message : 'Connection failed');
            // Check if the error is due to unauthorized access
            if (err && isUnauthorizedError(err)) {
                console.warn('[SignalR] Unauthorized error detected, attempting token refresh...');
                void handleAuthFailureRef.current?.();
            }
        }
    }, [createConnection, waitForDisconnected]);
    // Disconnect
    const disconnect = useCallback(async () => {
        const movie = movieConnectionRef.current;
        const timeline = timelineConnectionRef.current;
        if (movie)
            await movie.stop();
        if (timeline)
            await timeline.stop();
        movieConnectionRef.current = null;
        timelineConnectionRef.current = null;
        setStatus('Disconnected');
    }, []);
    // Helper to invoke hub methods safely
    const invokeMovie = useCallback(async (method, ...args) => {
        if (!movieConnectionRef.current || movieConnectionRef.current.state !== HubConnectionState.Connected) {
            throw new Error('SignalR not connected');
        }
        try {
            return await movieConnectionRef.current.invoke(method, ...args);
        }
        catch (err) {
            // Check if the error is due to unauthorized access
            if (isUnauthorizedError(err)) {
                console.warn('[SignalR] Unauthorized error during invoke, attempting token refresh...');
                void handleAuthFailureRef.current?.();
            }
            throw err;
        }
    }, []);
    const invokeTimeline = useCallback(async (method, ...args) => {
        if (!timelineConnectionRef.current || timelineConnectionRef.current.state !== HubConnectionState.Connected) {
            throw new Error('SignalR not connected');
        }
        try {
            return await timelineConnectionRef.current.invoke(method, ...args);
        }
        catch (err) {
            if (isUnauthorizedError(err)) {
                console.warn('[SignalR] Unauthorized error during invoke, attempting token refresh...');
                void handleAuthFailureRef.current?.();
            }
            throw err;
        }
    }, []);
    const handleAuthFailure = useCallback(async () => {
        const newToken = await refreshAccessToken();
        if (newToken) {
            await disconnect();
            await connect();
            return;
        }
        logout();
        navigate('/login', { replace: true });
    }, [connect, disconnect, logout, navigate]);
    handleAuthFailureRef.current = handleAuthFailure;
    const previousAccessTokenRef = useRef(accessToken);
    useEffect(() => {
        const previousToken = previousAccessTokenRef.current;
        previousAccessTokenRef.current = accessToken;
        if (!autoConnect ||
            !previousToken ||
            !accessToken ||
            previousToken === accessToken ||
            (status !== 'Connected' && status !== 'Reconnecting')) {
            return;
        }
        void (async () => {
            await disconnect();
            await connect();
        })();
    }, [accessToken, autoConnect, status, connect, disconnect]);
    // Auto-connect on mount
    useEffect(() => {
        if (!autoConnect) {
            return () => {
                disconnect();
            };
        }
        const timer = setTimeout(() => {
            connect();
        }, 0);
        return () => {
            clearTimeout(timer);
            disconnect();
        };
    }, [autoConnect, connect, disconnect]);
    // ============================================================================
    // Hub Method Wrappers
    // ============================================================================
    // Movie operations
    const moviesGetAll = useCallback(() => invokeMovie('MoviesGetAll'), [invokeMovie]);
    const moviesGetByName = useCallback((name) => invokeMovie('MoviesGetByName', name), [invokeMovie]);
    const movieSave = useCallback((movie) => invokeMovie('MovieSaveData', movie), [invokeMovie]);
    // Scene operations
    const scenesOfMovie = useCallback((movie) => invokeMovie('ScenesOfMovie', movie), [invokeMovie]);
    const sceneSave = useCallback((scene) => invokeMovie('SceneSaveData', scene), [invokeMovie]);
    const sceneCreate = useCallback((scene) => invokeMovie('SceneCreate', scene), [invokeMovie]);
    const sceneDelete = useCallback((scene) => invokeMovie('SceneDelete', scene), [invokeMovie]);
    const scenesResort = useCallback((scenes) => invokeMovie('ScenesResort', scenes), [invokeMovie]);
    // Library operations
    const libraryObjects = useCallback((movie) => invokeMovie('LibraryObjects', movie), [invokeMovie]);
    const libraryObjectSave = useCallback((item) => invokeMovie('LibraryObjectSave', item), [invokeMovie]);
    const libraryObjectGet = useCallback((id) => invokeMovie('LibraryObjectGet', id), [invokeMovie]);
    const libraryObjectDelete = useCallback((id) => invokeMovie('LibraryObjectDelete', id), [invokeMovie]);
    const libraryObjectFilePath = useCallback((item) => invokeMovie('LibraryObjectFilePath', item), [invokeMovie]);
    // SceneObject operations
    const sceneObjectsGet = useCallback((scene) => invokeMovie('SceneObjectsGet', scene), [invokeMovie]);
    const sceneObjectsGetByLayer = useCallback((scene) => invokeMovie('SceneObjectsGetByLayer', scene), [invokeMovie]);
    const sceneObjectCreateFromLibraryItem = useCallback((libraryObject, sceneId, layerId) => invokeMovie('SceneObjectCreateFromLibraryItem', libraryObject, sceneId, layerId), [invokeMovie]);
    const sceneObjectSave = useCallback((sceneObject) => invokeMovie('SceneObjectSave', sceneObject), [invokeMovie]);
    const sceneObjectUpdateLabel = useCallback((request) => invokeMovie('SceneObjectUpdateLabel', request), [invokeMovie]);
    const sceneObjectMoveToLayer = useCallback((request) => invokeMovie('SceneObjectMoveToLayer', request), [invokeMovie]);
    const sceneObjectUpdateHierarchy = useCallback((request) => invokeMovie('SceneObjectUpdateHierarchy', request), [invokeMovie]);
    const sceneObjectUpdatePivot = useCallback((request) => invokeMovie('SceneObjectUpdatePivot', request), [invokeMovie]);
    const sceneObjectSetVisibility = useCallback((request) => invokeMovie('SceneObjectSetVisibility', request), [invokeMovie]);
    const sceneObjectSetLock = useCallback((request) => invokeMovie('SceneObjectSetLock', request), [invokeMovie]);
    const sceneObjectsResort = useCallback((request) => invokeMovie('SceneObjectsResort', request), [invokeMovie]);
    const sceneObjectUpdateContent = useCallback((request) => invokeMovie('SceneObjectUpdateContent', request), [invokeMovie]);
    const sceneObjectsSave = useCallback((sceneObjects) => invokeMovie('SceneObjectsSave', sceneObjects), [invokeMovie]);
    const sceneObjectDelete = useCallback((sceneObject) => invokeMovie('SceneObjectDelete', sceneObject), [invokeMovie]);
    const sceneObjectDeleteBatch = useCallback((sceneObjects) => invokeMovie('SceneObjectDeleteBatch', sceneObjects), [invokeMovie]);
    const sceneObjectGetState = useCallback((sceneObject, keyFrameId) => invokeMovie('SceneObjectGetState', sceneObject, keyFrameId), [invokeMovie]);
    const sceneObjectSaveState = useCallback((state) => invokeMovie('SceneObjectSaveState', state), [invokeMovie]);
    const sceneObjectSaveStatesBatch = useCallback((states) => invokeMovie('SceneObjectSaveStatesBatch', states), [invokeMovie]);
    const sceneObjectPatchStateProperties = useCallback((request) => invokeMovie('SceneObjectPatchStateProperties', request), [invokeMovie]);
    const sceneEffectsGet = useCallback((scene) => invokeMovie('SceneEffectsGet', scene), [invokeMovie]);
    const sceneEffectSave = useCallback((request) => invokeMovie('SceneEffectSave', request), [invokeMovie]);
    const sceneEffectUpsertByType = useCallback((request) => invokeMovie('SceneEffectUpsertByType', request), [invokeMovie]);
    const sceneEffectPatchProperties = useCallback((request) => invokeMovie('SceneEffectPatchProperties', request), [invokeMovie]);
    const sceneEffectDelete = useCallback((effect) => invokeMovie('SceneEffectDelete', effect), [invokeMovie]);
    // Timeline Layer operations
    const timelineLayersOfScene = useCallback((scene) => invokeTimeline('TimelineLayersOfScene', scene), [invokeTimeline]);
    const timelineLayerCreate = useCallback((name, scene, sorter) => invokeTimeline('TimelineLayerCreate', name, scene, sorter), [invokeTimeline]);
    const timelineLayerSave = useCallback((layer) => invokeTimeline('TimelineLayerSave', layer), [invokeTimeline]);
    const timelineLayerSaveBatch = useCallback((layers) => invokeTimeline('TimelineLayerSaveBatch', layers), [invokeTimeline]);
    const timelineLayerDelete = useCallback((layer) => invokeTimeline('TimelineLayerDelete', layer), [invokeTimeline]);
    // Timeline sequence
    const timelineReadSceneSequence = useCallback((scene, fps) => invokeTimeline('TimelineReadSceneSequence', scene, fps), [invokeTimeline]);
    const timelineReadMovieSequence = useCallback((movie) => invokeTimeline('TimelineReadMovieSequence', movie), [invokeTimeline]);
    // KeyFrame operations
    const keyFrameSave = useCallback((keyFrame) => invokeTimeline('KeyFrameSave', keyFrame), [invokeTimeline]);
    const keyFrameDelete = useCallback((keyFrame) => invokeTimeline('KeyFrameDelete', keyFrame), [invokeTimeline]);
    // Tween operations
    const tweenSave = useCallback((tween) => invokeTimeline('TweenSave', tween), [invokeTimeline]);
    const tweenDelete = useCallback((tween) => invokeTimeline('TweenDelete', tween), [invokeTimeline]);
    // Clip operations
    const clipCreate = useCallback((sceneObjectId) => invokeMovie('ClipCreate', sceneObjectId), [invokeMovie]);
    const clipUpdate = useCallback((clip) => invokeMovie('ClipUpdate', clip), [invokeMovie]);
    const clipSetRootSceneObject = useCallback((clip, sceneObjectId) => invokeMovie('ClipSetRootSceneObject', clip, sceneObjectId), [invokeMovie]);
    return {
        status,
        error,
        connect,
        disconnect,
        // Movie
        moviesGetAll,
        moviesGetByName,
        movieSave,
        // Scene
        scenesOfMovie,
        sceneSave,
        sceneCreate,
        sceneDelete,
        scenesResort,
        // Library
        libraryObjects,
        libraryObjectSave,
        libraryObjectGet,
        libraryObjectDelete,
        libraryObjectFilePath,
        // SceneObject
        sceneObjectsGet,
        sceneObjectsGetByLayer,
        sceneObjectCreateFromLibraryItem,
        sceneObjectSave,
        sceneObjectUpdateLabel,
        sceneObjectMoveToLayer,
        sceneObjectUpdateHierarchy,
        sceneObjectUpdatePivot,
        sceneObjectSetVisibility,
        sceneObjectSetLock,
        sceneObjectsResort,
        sceneObjectUpdateContent,
        sceneObjectsSave,
        sceneObjectDelete,
        sceneObjectDeleteBatch,
        sceneObjectGetState,
        sceneObjectSaveState,
        sceneObjectSaveStatesBatch,
        sceneObjectPatchStateProperties,
        sceneEffectsGet,
        sceneEffectSave,
        sceneEffectUpsertByType,
        sceneEffectPatchProperties,
        sceneEffectDelete,
        // Timeline Layer
        timelineLayersOfScene,
        timelineLayerCreate,
        timelineLayerSave,
        timelineLayerSaveBatch,
        timelineLayerDelete,
        timelineReadSceneSequence,
        timelineReadMovieSequence,
        // KeyFrame
        keyFrameSave, //create+move 
        keyFrameDelete,
        // Tween
        tweenSave, //create+move 
        tweenDelete,
        // Clip
        clipCreate,
        clipUpdate,
        clipSetRootSceneObject,
    };
}
// ============================================================================
// Conversion Helpers (Response DTOs → Frontend UI types)
// ============================================================================
/** Convert SceneObjectResponse to frontend SceneObject for UI rendering */
function convertSceneObjectResponse(response) {
    const defaultState = response.defaultState;
    let props = {};
    if (defaultState?.properties) {
        try {
            props = JSON.parse(defaultState.properties);
        }
        catch {
            // Invalid JSON, use defaults
        }
    }
    const rawSourcePath = response.url ?? response.fullPath ?? extractAssetPathFromBody(response.body);
    const resolvedSrc = rawSourcePath ? resolveAssetUrl(rawSourcePath) : '';
    const x = Math.round(props.left ?? props.x ?? response.dropPosition?.left ?? 0);
    const y = Math.round(props.top ?? props.y ?? response.dropPosition?.top ?? 0);
    const width = normalizeObjectSize(props.width ?? response.width);
    const height = normalizeObjectSize(props.height ?? response.height);
    const rotation = props.rotation ?? 0;
    const opacity = props.opacity ?? 1;
    return {
        id: String(response.id),
        type: response.type || 'image',
        name: response.name,
        src: resolvedSrc || undefined,
        libraryObjectId: response.libraryObjectId ?? undefined,
        parentObjectId: response.parentObjectId != null ? String(response.parentObjectId) : null,
        isGroup: response.isGroup === true || response.type === 'group',
        x,
        y,
        width,
        height,
        rotation,
        opacity,
        zIndex: response.sorter ?? 0,
        visible: response.isVisible !== false,
        locked: response.isLocked === true,
        pivotPoint: response.pivotPoint?.trim() ? response.pivotPoint : 'center',
        layerId: response.layerId,
        defaultPose: { x, y, width, height, rotation, opacity },
        states: [],
    };
}
/** Convert MovieResponse to frontend Movie for UI rendering */
export function convertMovieResponse(response) {
    const width = response.width > 0 ? response.width : 500;
    const height = response.height > 0 ? response.height : 500;
    return {
        id: String(response.id),
        name: response.name,
        description: response.description ?? '',
        notes: '',
        width,
        height,
        fps: response.fps,
        scenes: response.scenes.map(convertSceneResponse),
    };
}
/** Convert SceneResponse to frontend Scene for UI rendering */
export function convertSceneResponse(response) {
    return {
        id: String(response.id),
        name: response.name,
        order: response.sorter,
        objects: [],
    };
}
/** Convert LibraryObjectResponse to frontend LibraryItem for UI rendering */
export function convertLibraryObjectResponse(response) {
    const src = response.url
        ? resolveAssetUrl(response.url)
        : response.fullPath
            ? resolveAssetUrl(response.fullPath)
            : '';
    const thumbnail = response.thumbnailUrl ? resolveAssetUrl(response.thumbnailUrl) : undefined;
    return {
        id: String(response.id),
        name: response.name,
        type: response.type,
        src,
        thumbnail,
    };
}
// ============================================================================
// Export default for convenience
// ============================================================================
export default useSignalR;
